// Hooks do domínio de ACESSO e PAPÉIS por marca: o que o viewer pode
// (/v1/access/me), o catálogo de permissões que desenha o editor
// (/v1/access/catalog) e os papéis customizáveis da marca (/v1/roles).
// Mesmo padrão de use-brands.ts / use-calls.ts:
//   - `src/api/roles.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRole,
  deleteRole,
  getAccessMe,
  getPermissionCatalog,
  listRoles,
  updateRole,
  type CustomRole,
  type RoleInput,
} from "@/api/roles";
import { getAppAccess, invalidateAppAccess } from "@/lib/auth-client";
import { ACCESS_CATALOG_QUERY_KEY, ACCESS_ME_QUERY_KEY } from "@/lib/access-query-keys";

// As chaves DESTE domínio, exatamente como já estavam nas telas.
//
// `me` e `catalog` NÃO são declaradas aqui: elas continuam vindo de
// `lib/access-query-keys.ts`, e isso é de propósito. Quem as escreve primeiro é
// o bootstrap da sessão (lib/auth-client.ts, via `ensureQueryData`), e
// auth-client não pode importar este arquivo — este arquivo importa
// `invalidateAppAccess`/`getAppAccess` DE auth-client, e o ciclo se fecharia.
// Mesmo dilema que a onda 1 teve com `["brands","mine"]`, com uma diferença que
// vale a pena: lá a chave acabou DUPLICADA (uma const privada em auth-client +
// `brandKeys.mine` aqui); aqui já existia um módulo sem dependência nenhuma
// (`lib/access-query-keys.ts`) que os dois lados importam, então a factory
// aponta para ele em vez de repetir os literais. Uma fonte só, sem ciclo.
export const accessKeys = {
  me: ACCESS_ME_QUERY_KEY,
  catalog: ACCESS_CATALOG_QUERY_KEY,
  // Snapshot de acesso do gate de assinatura. Divergência pré-existente
  // preservada: a queryFn é `getAppAccess` (o cache SWR de auth-client), não
  // uma função de api — e a chave não compartilha prefixo com `me`, então
  // invalidar ["access", …] não a derruba. Só a tela de assinatura a usa.
  appRole: ["app-access-role"] as const,
};

export const roleKeys = {
  list: ["roles"] as const,
};

// GET /v1/roles — todos os papéis da marca ativa (os três de sistema + os
// criados pela marca). É daqui que sai o seletor de papel da tela de usuários:
// nunca de um array fixo com os três literais.
export function useRoles() {
  return useQuery({ queryKey: roleKeys.list, queryFn: listRoles });
}

// GET /v1/access/me. Mesma chave do bootstrap (ver accessKeys), então em
// operação normal é cache, sem request extra.
export function useAccessMe() {
  return useQuery({ queryKey: accessKeys.me, queryFn: getAccessMe });
}

// GET /v1/access/catalog. `enabled` para o editor de papéis, que só busca o
// catálogo quando o diálogo abre.
export function usePermissionCatalog(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: accessKeys.catalog,
    queryFn: getPermissionCatalog,
    enabled: options.enabled,
  });
}

// Papel-base do viewer para o gate de assinatura, lido do cache SWR de
// auth-client (não de /v1/access/me — ver a ressalva em accessKeys.appRole).
export function useAppAccessRole() {
  return useQuery({ queryKey: accessKeys.appRole, queryFn: () => getAppAccess() });
}

// Criar e editar papel são a MESMA mutação do ponto de vista do cache: `id`
// ausente cria, presente edita. As quatro consequências abaixo valem para todo
// chamador, então são do hook:
//   - a lista de papéis muda;
//   - quem editou o PRÓPRIO papel precisa do /access/me e do catálogo de volta
//     (os dois têm 5min de staleTime, e nada os invalidava ao salvar);
//   - o guard de rota (lib/app-guard.ts) não lê o TanStack Query: ele lê o
//     cache SWR próprio de getAppAccess, daí o invalidateAppAccess().
// A seta no mutationFn é obrigatória: o TanStack chama o mutationFn com
// (variáveis, contexto), e o contexto entraria como 2º argumento da api.
export function useSaveRole(
  handlers: { onSuccess?: (role: CustomRole) => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RoleInput & { id?: string }) =>
      id ? updateRole(id, input) : createRole(input),
    onSuccess: (role) => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.list });
      void queryClient.invalidateQueries({ queryKey: accessKeys.me });
      void queryClient.invalidateQueries({ queryKey: accessKeys.catalog });
      invalidateAppAccess();
      handlers.onSuccess?.(role);
    },
    onError: handlers.onError,
  });
}

// DELETE /v1/roles/:id — soft delete, recusado (409) se algum membro ativo
// ainda usa o papel. Só a lista muda: um papel sem membros não altera o acesso
// de ninguém.
export function useDeleteRole(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      void queryClient.invalidateQueries({ queryKey: roleKeys.list });
    },
    onError: handlers.onError,
  });
}
